import uniqBy from 'lodash/uniqBy';
import { GetOmsPayload, State } from '../types';
import { isJiraProduct, isConfluenceProduct } from '@atlassian/cssxp-utils-product';
import { CartLinkedEntitlementsSchema, CartProductSchema } from '../../types';
import { getAppIdentifier, stitchChangeKeyObjects } from './utils';
import { ROVO_PRODUCT_ID } from '@css-xp/ppc-constants';
export const getSandboxProductParentIdentifier = (product: CartProductSchema) => {
  if (isJiraProduct(product.productId) && product.containerId) {
    return product.containerId;
  }
  if (isConfluenceProduct(product.productId) || product.productId === ROVO_PRODUCT_ID) {
    return product.id;
  }
  return '';
};
export const getLinkedSandboxProductsAndAppsToProd = (
  product: CartProductSchema,
  { sandboxProducts, sandboxApps }: Partial<State>
): { products: CartProductSchema[]; linkedEntitlements: CartLinkedEntitlementsSchema[] } => {
  if (!sandboxProducts || !Object.values(sandboxProducts).length) {
    return {
      products: [],
      linkedEntitlements: [],
    };
  }
  const sandboxLinkedProducts = Object.values(sandboxProducts)?.filter(
    (sandboxProduct) =>
      sandboxProduct.sandboxParent && sandboxProduct.sandboxParent === getSandboxProductParentIdentifier(product)
  );

  const sandboxLinkedApps: CartLinkedEntitlementsSchema[] = [];
  sandboxLinkedProducts.forEach((sandboxProduct) => {
    const appIdentifier = getAppIdentifier(sandboxProduct);
    sandboxLinkedApps.push(...(sandboxApps?.[appIdentifier] ?? []));
  });
  return {
    products: sandboxLinkedProducts,
    linkedEntitlements: uniqBy(sandboxLinkedApps, 'id'),
  };
};

export const getSandboxEntitlementsLinkedToProducts = (
  selectedProducts: CartProductSchema[],
  {
    sandboxAppsObj,
    sandboxProductsObj,
  }: {
    sandboxAppsObj: State['sandboxApps'];
    sandboxProductsObj: State['sandboxProducts'];
  }
) => {
  const linkedProducts: CartProductSchema[] = [];
  const linkedApps: CartLinkedEntitlementsSchema[] = [];
  selectedProducts.forEach((product) => {
    const { linkedEntitlements, products } = getLinkedSandboxProductsAndAppsToProd(product, {
      sandboxApps: sandboxAppsObj,
      sandboxProducts: sandboxProductsObj,
    });
    linkedApps.push(...linkedEntitlements);
    linkedProducts.push(...products);
  });
  const uniqProducts = uniqBy(linkedProducts, 'id');
  const uniqApps = uniqBy(linkedApps, 'id');

  return {
    products: uniqProducts,
    linkedEntitlements: uniqApps,
  };
};
// export const getSandboxEntitlementsPartnerPromotions = async (
//   selectedProducts: CartProductSchema[],
//   {
//     sandboxAppsObj,
//     sandboxProductsObj,
//     existingPromotions,
//     selectedPartnerAccountData,
//     areCorporateResellersOrAggregatorsAllowed = false,
//   }: {
//     sandboxAppsObj: State['sandboxApps'];
//     sandboxProductsObj: State['sandboxProducts'];
//     existingPromotions: State['partnerPromotions'];
//     selectedPartnerAccountData: State['selectedPartnerAccountData'];
//     areCorporateResellersOrAggregatorsAllowed?: boolean;
//   }
// ) => {
//   const linkedProducts: CartProductSchema[] = [];
//   const linkedApps: CartLinkedEntitlementsSchema[] = [];
//   selectedProducts.forEach((product) => {
//     const { apps, products } = getLinkedSandboxProductsAndAppsToProd(product, {
//       sandboxApps: sandboxAppsObj,
//       sandboxProducts: sandboxProductsObj,
//     });
//     linkedApps.push(...apps);
//     linkedProducts.push(...products);
//   });
//   const uniqProducts = uniqBy(linkedProducts, 'id');
//   const uniqApps = uniqBy(linkedApps, 'id');
//
//   return await getPartnerPromotions({
//     selectedProducts: uniqProducts,
//     selectedAppsList: uniqApps,
//     existingPromotions,
//     selectedPartnerAccountData,
//     areCorporateResellersOrAggregatorsAllowed,
//   });
// };

export const getSandboxOmsPayload = ({
  cartProducts,
  sandboxProducts,
  sandboxApps,
}: Pick<GetOmsPayload, 'cartProducts' | 'sandboxProducts' | 'sandboxApps'>): Partial<GetOmsPayload> => {
  const containerIdOrConfluenceIdInCart =
    cartProducts?.reduce((acc: Record<string, Partial<CartProductSchema>>, product) => {
      const identifier = getSandboxProductParentIdentifier(product);
      if (!identifier) {
        return acc;
      }
      acc[identifier] = {
        // FIXME:: handle the case of bill date change
        productStatuses: product.productStatuses,
        nextBillDate: product.nextBillDate,
      };
      return acc;
    }, {}) ?? {};

  const sandboxSelectedProducts = Object.values(sandboxProducts)
    ?.filter(
      (sandboxProduct) =>
        sandboxProduct.sandboxParent && !!containerIdOrConfluenceIdInCart[sandboxProduct.sandboxParent]
    )
    .reduce((selectedProductObj: State['selectedProducts'], product) => {
      selectedProductObj[product.id] = containerIdOrConfluenceIdInCart[product.sandboxParent as string];
      return selectedProductObj;
    }, {});

  const sandboxSelectedApps = Object.keys(sandboxSelectedProducts).reduce(
    (selectedAppObj: NonNullable<State['selectedLinkedEntitlements']>, productId) => {
      const sandboxAppsArr = sandboxApps?.[productId];
      sandboxAppsArr?.forEach((app) => {
        // FIXME:: handle the case of bill date change
        selectedAppObj[app.id] = sandboxSelectedProducts[productId] as Partial<CartLinkedEntitlementsSchema>;
      });
      return selectedAppObj;
    },
    {}
  );

  const sandboxCartProducts = Object.values(
    stitchChangeKeyObjects<CartProductSchema>(sandboxProducts, sandboxSelectedProducts)
  );

  return {
    initialStoreProducts: sandboxProducts,
    cartProducts: sandboxCartProducts,
    cartApps: sandboxApps,
    selectedProducts: sandboxSelectedProducts,
    selectedLinkedEntitlements: sandboxSelectedApps,
  };
};
